import type { TicketProduct } from '@gatecheckpro/contracts';

/**
 * Which ticket products can back reserved seating for one session, and why the
 * others cannot.
 *
 * R1 makes a reserved-seat **product** single-session, and the unit matters:
 * the rule binds the product, not merely one of its versions. Two certified
 * constraints put it there — `ticket_product_versions_one_current` and
 * `inventory_allocations_one_saleable_per_product` — and together they mean a
 * product has one current version drawing on one saleable allocation. A product
 * admitting to Friday *and* Saturday therefore has a single allocation, and one
 * allocation cannot hold both "12A is free on Friday" and "12A is free on
 * Saturday". Widening either would change the general-admission capacity model
 * that is already certified.
 *
 * So the organizer's choice here is a **product**. The exact immutable version
 * carrying the price and terms is resolved and frozen by the server at
 * activation; it is shown, because an organizer should see what they are
 * pinning, but it is not the thing being picked.
 *
 * **This is help, not authority.** The server decides whether a product is
 * single-session, whether its session matches, whether it has terms to freeze,
 * and whether it is already bound to another night — and it decides those at
 * save and activation, against state that may have moved since this page
 * loaded. Nothing here is consulted before a command is sent, and a refusal is
 * rendered from the server's typed reason rather than pre-empted from this.
 *
 * What it is for: telling an organizer *now* rather than after a round trip,
 * and telling them why. A product missing from a dropdown with no explanation
 * is indistinguishable from a bug, and somebody will go looking for one.
 */
export type SeatingIneligibility =
  | 'ADMITS_TO_EVERY_SESSION'
  | 'ADMITS_TO_ANOTHER_SESSION'
  | 'ADMITS_TO_SEVERAL_SESSIONS'
  | 'NOT_ON_SALE';

export interface ProductEligibility {
  readonly product: TicketProduct;
  readonly eligible: boolean;
  readonly reason: SeatingIneligibility | null;
}

/** What to tell an organizer, in the terms of the decision they would make. */
export const INELIGIBILITY_NOTE: Readonly<Record<SeatingIneligibility, string>> = Object.freeze({
  ADMITS_TO_EVERY_SESSION:
    'Admits to every session. A reserved seat belongs to one night, so this needs a product of its own for this session.',
  ADMITS_TO_ANOTHER_SESSION: 'Belongs to a different night of this event.',
  ADMITS_TO_SEVERAL_SESSIONS:
    'Admits to several nights at once. Reserved seating uses one product per night — they can share a name and a price and still be separate products.',
  NOT_ON_SALE: 'Not published, so nothing can go on sale against it yet.',
});

export function eligibilityFor(
  products: readonly TicketProduct[],
  eventSessionId: string,
): ProductEligibility[] {
  return products.map((product) => {
    const version = product.currentVersion;
    const admits = version.sessionIds;

    const reason: SeatingIneligibility | null =
      product.status !== 'PUBLISHED'
        ? 'NOT_ON_SALE'
        : version.accessScope === 'ALL_SESSIONS'
          ? 'ADMITS_TO_EVERY_SESSION'
          : admits.length > 1
            ? 'ADMITS_TO_SEVERAL_SESSIONS'
            : admits[0] === eventSessionId
              ? null
              : 'ADMITS_TO_ANOTHER_SESSION';

    return { product, eligible: reason === null, reason };
  });
}
